<template>
  <div class="filter">
    <div class="date-filter">
      <VueDatePicker
        v-model="selectedDate"
        :format="'yyyy-MM-dd'"
        :enable-time-picker="false"
        :dark="isDarkTheme"
        :clearable="true"
        :placeholder="t('result_view.response_time_analysis.datepicker_placeholder')"
        class="date-picker"
      />
    </div>
  </div>
  <div v-if="!hasAnyData">
    <div v-if="dataStore.oneFileSelected" class="no-data-screen">
      {{ t('result_view.response_time_analysis.no_errors_selected') }}
    </div>
    <div v-else class="no-data-screen">
      {{ t('result_view.response_time_analysis.no_errors') }}
    </div>
  </div>
  <div v-else>
    <TreeTable
      :value="filteredNodes"
      :sortField="sortField"
      :sortOrder="sortOrder"
      removable-sort
      class="rt-table response-time-analysis-table"
    >
      <Column
        field="day"
        :header="t('result_view.response_time_analysis.day_file')"
        sortable
        class="entry-left-edge entry-item"
      >
        <template #body="{ node }">
          <span style="padding-left: 10px;">{{ node.data.day }}</span>
        </template>
      </Column>

      <Column
        field="avgMs"
        :header="t('result_view.response_time_analysis.avg')"
        sortable
        class="entry-item"
      >
        <template #body="{ node }">
          <span>{{ formatDuration(node.data.avgMs) }}</span>
        </template>
      </Column>

      <Column
        field="topTenAvgMs"
        :header="t('result_view.response_time_analysis.topten')"
        sortable
        class="entry-item"
      >
        <template #body="{ node }">
          <span>{{ formatDuration(node.data.topTenAvgMs) }}</span>
          <span
            class="info-tooltip-button"
            @click.stop="openTopTen(node)"
           >
            <img src="@/assets/infoIcon.svg" alt="info icon" class="info-tooltip-icon">
          </span>
        </template>
      </Column>

      <Column
        field="unresolvedCount"
        :header="t('result_view.response_time_analysis.unresolved')"
        sortable
        class="entry-item"
      >
        <template #body="{ node }">
          <span>{{ node.data.unresolvedCount > 0 ? node.data.unresolvedCount : '' }}</span>
          <span
            v-if="node.data.unresolvedCount > 0"
            class="info-tooltip-button"
            @click.stop="openUnresolved(node)"
          >
            <img src="@/assets/infoIcon.svg" alt="info icon" class="info-tooltip-icon">
          </span>
        </template>
      </Column>

      <Column
        field="exceededCount"
        :header="`${t('result_view.response_time_analysis.exceeded')} (${dataStore.maxTraTimeSec ? dataStore.maxTraTimeSec : '10'}s)`"
        sortable
        class="entry-item"
      >
        <template #body="{ node }">
          <span>{{ node.data.exceededCount > 0 ? node.data.exceededCount : '' }}</span>
          <span
            v-if="node.data.exceededCount > 0"
            class="info-tooltip-button"
            @click.stop="openExceeded(node)"
          >
            <img src="@/assets/infoIcon.svg" alt="info icon" class="info-tooltip-icon">
          </span>
        </template>
      </Column>
    </TreeTable>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useDataStore } from '@/stores/dataStore'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import VueDatePicker from '@vuepic/vue-datepicker'
import TreeTable from 'primevue/treetable'
import Column from 'primevue/column'

const { t } = useI18n()
const dataStore = useDataStore()
const { responseTimes } = storeToRefs(dataStore)
const theme = ref(localStorage.getItem('user-theme') || 'light-theme')
const isDarkTheme = computed(() => theme.value === 'dark-theme')

// Date filter
const selectedDate = ref(null) // single day
const hasAnyData = computed(() => (responseTimes.value?.length || 0) > 0)

const emit = defineEmits(['open-modal'])
function openModalWithLines(lines) {
  emit('open-modal', {
    data: { Lines: Array.isArray(lines) ? lines : [] },
    hideCopyButton: true
  })
}

function openTopTen(node) {
  openModalWithLines(node.data.topTenLines ?? [])
}

function openUnresolved(node) {
  const byType = node.data.unresolvedByType || {}
  const lines = Object.entries(byType).map(([type, count]) => `${type}: ${count}`)
  openModalWithLines(lines)
}

function openExceeded(node) {
  openModalWithLines(node.data.exceededLines ?? [])
}

function ymd(dateObj) {
  const d = new Date(dateObj)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function formatDuration(ms) {
  const value = Number(ms)
  if (!Number.isFinite(value)) return ''

  const seconds = value / 1000
  const absSec = Math.abs(seconds)

  let unit = 's'
  let displayValue = seconds

  if (absSec >= 24 * 60 * 60) {
    unit = 'd'
    displayValue = seconds / (24 * 60 * 60)
  } else if (absSec >= 60 * 60) {
    unit = 'h'
    displayValue = seconds / (60 * 60)
  } else if (absSec >= 60) {
    unit = 'min'
    displayValue = seconds / 60
  }

  const minimumFractionDigits = absSec < 1 ? 3 : 0

  const formatted = displayValue.toLocaleString(undefined, {
    maximumFractionDigits: 3,
    minimumFractionDigits
  })

  return `${formatted} ${unit}`
}

const filteredNodes = computed(() => {
  if (!selectedDate.value) return responseTimes.value
  return responseTimes.value.filter(n => n.data.day === ymd(selectedDate.value))
})

const sortField = ref('day')
const sortOrder = ref(-1) // newest first

onMounted(() => {
  window.addEventListener('theme-changed', syncTheme)
  window.addEventListener('storage', syncTheme)
})

onUnmounted(() => {
  window.removeEventListener('theme-changed', syncTheme)
  window.removeEventListener('storage', syncTheme)
})

function syncTheme() {
  theme.value = localStorage.getItem('user-theme') || 'light-theme'
}
</script>

<style>
.response-time-analysis-table .p-treetable-tbody > tr[aria-level="1"] {
  background: var(--light-grey-background-color) !important;
  padding: 5px 0;
  margin-top: 10px;
  height: 48px;
}

.date-picker {
  min-width: 220px;
}

.info-btn {
  margin-left: .5rem;
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 9999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: .75rem;
  border: 1px solid #179DE0;
  color: #179DE0;
  background: transparent;
  transition: transform .12s ease;
}

.info-btn:hover {
  transform: scale(1.06);
}

.entry-item {
  width: 20%;
}

.no-data-screen {
  font-size: 20px;
  text-align: center;
  margin-top: 20px;
}

.entry-left-edge {
  border-radius: 4px 0px 0px 4px;
  min-width: 230px;
}

.entry-right-edge {
  border-radius: 0px 4px 4px 0px;
  flex-grow:1;
  word-wrap: break-word;
}

.info-tooltip-button {
  padding: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
}

.info-tooltip-button:hover {
  background: transparent;
  border: none;
}

.info-tooltip-icon {
  height: 20px;
  width: 20px;
}

.tab-view::-webkit-scrollbar {
  width: 10px;
}

.tab-view::-webkit-scrollbar-track {
  background-color: var(--background-color-secondary);
  border-radius: 10px 10px 0 0;
}

.tab-view::-webkit-scrollbar-thumb {
  background-color: var(--scrollbar-thumb);
  border-radius: 10px;
}

.tab-view::-webkit-scrollbar-thumb:hover {
  background-color: var(--scrollbar-thumb-hover);
}
</style>
